import {format} from 'date-fns';
import isDate from 'date-fns/isDate';
import lastDayOfWeek from 'date-fns/lastDayOfWeek';
import {cs} from 'date-fns/locale';
import parseISO from 'date-fns/parseISO';
import startOfWeek from 'date-fns/startOfWeek';
import React, {useEffect, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import Cell from '../../components/Cell/Cell';
import CellGroup from '../../components/CellGroup/CellGroup';
import Empty from '../../components/Empty/Empty';
import List from '../../components/List/List';
import ListSummary from '../../components/ListSummary/ListSummary';
import GroupDay from '../../components/ProjectTimeDay/ProjectTimeDay';
import Total from '../../components/UI/Total/Total';
import WorkThumbnail from '../../components/WorkThumbnail/WorkThumbnail';
import {formatDecimalNumber, formattedTime} from '../../helpers/helpers';
import {dateToTimestamp, totalTimesTime, totalWorkPrices} from '../../helpers/utils';
import {timeFetch} from '../../store/actions';
import Page from '../../components/Page/Page';
import PageTitle from '../../components/PageTitle/PageTitle';
import Section from '../../components/Section/Section';
import InputDateRange from '../../components/UI/InputDate/InputDateRange';

const User = () => {
  const [activeUser, setActiveUser] = useState(null);
  const [dateFrom, setDateFrom] = useState(startOfWeek(new Date()), {locale: cs});
  const [dateTo, setDateTo] = useState(lastDayOfWeek(new Date()), {locale: cs});

  const dispatch = useDispatch();

  const {works, uid, members} = useSelector(
    state => ({
      works: state.times.times,
      uid: state.auth.uid,
      members: state.team.members,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (members && uid) {
      const member = members.find(member => {
        return member.user.id === uid;
      });

      setActiveUser(member);
    } else {
      setActiveUser(null);
    }
  }, [uid, members]);

  useEffect(() => {
    if (isDate(dateFrom) && isDate(dateTo) && activeUser !== undefined && activeUser !== null) {
      dispatch(timeFetch(dateToTimestamp(dateFrom), dateToTimestamp(dateTo), activeUser.id));
    }
  }, [dateFrom, dateTo, activeUser]);

  // Create groups of times by day.
  const groups = works.reduce((groups, time) => {
    const date = time.day.split(' ')[0];
    if (!groups[date]) {
      // eslint-disable-next-line no-param-reassign
      groups[date] = [];
    }
    groups[date].push(time);
    return groups;
  }, {});

  const worksTimes = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(groups)) {
    const date = parseISO(key);
    const formatedDate = format(date, 'dd. MM. yyyy');
    worksTimes.push(<GroupDay key={`date-${formatedDate}`} day={formatedDate} />);

    const listItems = Object.values(value).map(time => {
      return <WorkThumbnail key={`time-${time.id}`} time={time} />;
    });

    const totalLoggedTime = totalTimesTime(value);
    const workPrices = totalWorkPrices(value);

    worksTimes.push(
      <List type='row' key={`list-${key}`}>
        {listItems}
        <ListSummary type='small'>
          <CellGroup type='info' />
          <CellGroup type='quantity'>
            <Cell type='day' align='right'>
              <Total>{formattedTime(totalLoggedTime)}</Total>
            </Cell>
          </CellGroup>
          <CellGroup type='prices'>
            <Cell type='price' align='right'>
              <Total>{formatDecimalNumber(workPrices.totalBuy)}</Total>
            </Cell>
            <Cell type='price' align='right'>
              <Total>{formatDecimalNumber(workPrices.totalSell)}</Total>
            </Cell>
          </CellGroup>
        </ListSummary>
      </List>
    );
  }

  const totalLoggedTime = totalTimesTime(works);
  const workPrices = totalWorkPrices(works);

  const hasTermsSelected = dateFrom != null && dateTo != null;
  const canDisplayWorks = works.length > 0;

  const emptyWorks = (
    <Empty title='Žádná práce'>Ve vybraném termínu nebyla nalezeny žádná práce.</Empty>
  );

  const emptyTerms = <Empty title='Zvolte termín'>Zvolte termín pro zobrazení práce.</Empty>;

  const projectWorksFooter = (
    <ListSummary>
      <CellGroup type='info' />
      <CellGroup type='quantity'>
        <Cell type='day' align='right'>
          <Total>{formattedTime(totalLoggedTime)}</Total>
        </Cell>
      </CellGroup>
      <CellGroup type='prices'>
        <Cell type='price' align='right'>
          <Total>{formatDecimalNumber(workPrices.totalBuy)}</Total>
        </Cell>
        <Cell type='price' align='right'>
          <Total>{formatDecimalNumber(workPrices.totalSell)}</Total>
        </Cell>
      </CellGroup>
    </ListSummary>
  );

  return (
    <Page>
      <Section type='no_border'>
        <PageTitle title={activeUser ? activeUser.user.name : 'Unknown'} />
      </Section>
      <Section type='filters'>
        <div className='filters'>
          <div className='filters__section filters__section--fulltext'>
            <InputDateRange
              startDate={dateFrom}
              endDate={dateTo}
              onStartDateSelect={setDateFrom}
              onEndDateSelect={setDateTo}
            />
          </div>
        </div>
      </Section>
      <Section type='no_border'>
        {!hasTermsSelected ? emptyTerms : null}
        {hasTermsSelected && !canDisplayWorks ? emptyWorks : null}
        {canDisplayWorks ? worksTimes : null}
        {canDisplayWorks ? projectWorksFooter : null}
      </Section>
    </Page>
  );
};
export default User;
